import { RenderLayer } from '@skand/three-js-renderer';
import { Matrix4, Quaternion, Vector3 } from 'three';
/**
 * Panorama editor controller.
 */
export class PanoramaEditor {
    constructor(renderer, gestures, navigationService) {
        Object.defineProperty(this, "target", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "gestures", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "navigationService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pointer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "grabbing", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "running", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "currentNavigationMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "currentProjectionMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.target = null;
        this.renderer = renderer;
        this.gestures = gestures;
        this.navigationService = navigationService;
        this.pointer = this.renderer.getObjectFactory().createSphere({
            radius: 0.15,
            material: {
                type: 'basicAlpha',
                color: { r: 1, g: 1, b: 1 },
                alpha: 0.4,
            },
            position: {
                ecef: { x: 0, y: 0, z: 0 },
            },
        });
        this.pointer.setRenderLayer(RenderLayer.AfterPanorama);
        this.grabbing = false;
        this.running = false;
        this.currentNavigationMode = this.navigationService.getNavigationMode();
        this.currentProjectionMode = this.navigationService.getProjectionMode();
    }
    /**
     * Start the editor.
     *
     * @param panorama
     * @param onEdit
     */
    async start(panorama, onEdit) {
        this.stop();
        this.target = panorama;
        this.running = true;
        try {
            await this.target.show360();
            if (!this.running) {
                this.stop();
                return;
            }
        }
        catch {
            this.stop();
            return;
        }
        this.currentNavigationMode = this.navigationService.getNavigationMode();
        this.currentProjectionMode = this.navigationService.getProjectionMode();
        this.navigationService.setProjectionMode('perspective');
        this.navigationService.setNavigationMode('panorama');
        this.navigationService.moveTo(this.target.getPosition(), this.navigationService.getRotation());
        this.renderer.getWorld().addObject(this.pointer);
        this.gestures.register('keytap', gesture => {
            if (this.target === null)
                return;
            const camera = this.renderer.getWorld().getCamera();
            // Movement axes
            const matrix = new Matrix4();
            camera.getWorldMatrix(matrix);
            const right = new Vector3();
            const up = new Vector3();
            const back = new Vector3();
            matrix.extractBasis(right, up, back);
            const currentPosition = this.navigationService.getPosition();
            const currentRotation = this.navigationService.getRotation();
            const speed = 0.01;
            if (gesture.key === 'w')
                currentPosition.sub(back.multiplyScalar(speed));
            if (gesture.key === 'a')
                currentPosition.sub(right.multiplyScalar(speed));
            if (gesture.key === 's')
                currentPosition.add(back.multiplyScalar(speed));
            if (gesture.key === 'd')
                currentPosition.add(right.multiplyScalar(speed));
            if (gesture.key === 'q')
                currentPosition.sub(up.multiplyScalar(speed));
            if (gesture.key === 'e')
                currentPosition.add(up.multiplyScalar(speed));
            this.navigationService.moveTo(currentPosition, currentRotation);
            this.target.setPosition(currentPosition);
        });
        // Handle toggling camera orientation grab mode
        this.gestures.register('tap', gesture => {
            if (gesture.button === 2) {
                this.grabbing = true;
            }
        });
        this.gestures.register('release', () => {
            this.grabbing = false;
        });
        // Submit button
        this.gestures.register('keytap', gesture => {
            if (gesture.key == ' ' && this.target) {
                onEdit(this.target);
                this.stop();
            }
        });
        // Handle the 3D pointer and grabbing the camera
        let prevCameraRotation = this.navigationService.getRotation();
        this.gestures.register('hover', gesture => {
            const pick = this.renderer.pickScreenWorldSpaceSnapping(gesture.position);
            this.pointer.setPositionECEF(pick.position);
            if (this.target === null)
                return;
            // Calculate the delta rotation quaternion for the camera
            // rotationDelta * previousRotation = cameraRotation
            const cameraRotation = this.navigationService.getRotation();
            const rotationDelta = new Quaternion().multiplyQuaternions(cameraRotation, prevCameraRotation.invert());
            prevCameraRotation = cameraRotation;
            // Adjust the orientation of the panorama if camera is grabbed
            if (this.grabbing) {
                const targetRotation = this.target.getRotation();
                const newRotation = rotationDelta.multiply(targetRotation).normalize();
                this.target.setRotation(newRotation);
            }
        });
    }
    /**
     * Stop the editor.
     */
    stop() {
        this.running = false;
        this.gestures.unregisterAll();
        this.navigationService.setNavigationMode(this.currentNavigationMode);
        this.navigationService.setProjectionMode(this.currentProjectionMode);
        this.renderer.getWorld().removeObject(this.pointer);
        this.target?.hide360();
    }
    /**
     * Check if the editor is active.
     *
     * @returns
     */
    isActive() {
        return this.running;
    }
}
